import type { RouterContextType } from "@/api/permissions/permissions.api";
import type { ModuleKey } from "@/api/permissions/permissions.types";
import { AddOrEditRoleModal } from "@/components/features/RolesPermissions/AddOrEditRoleModal";
import { ModulesSidebar } from "@/components/features/RolesPermissions/ModulesSidebar";
import { PermissionsTable } from "@/components/features/RolesPermissions/PermissionsTable";
import { RolePreviewOverlay } from "@/components/features/RolesPermissions/RolePreviewOverlay";
import { RoleScopeFields } from "@/components/features/RolesPermissions/RoleScopeTab/RoleScopeFields";
import { Button } from "@/components/ui/Button/Button";
import { Card } from "@onengine/component-library";
import { TableCard } from "@/components/ui/TableCard/TableCard";
import Loader from "@/components/ui/Loader/Loader";
import { useCreateRole } from "@/hooks/roles-permissions/useRolesPermissionsMutations";
import { getRoleById, useAllModules } from "@/hooks/roles-permissions/useRolesPermissionsQueries";
import { effectivePermissionIds } from "@/api/roles-permissions/permissionCatalog";
import {
  roleWriteFor,
  RoleWriteRefusedError,
  switchesForSave,
} from "@/api/roles-permissions/roleWrite";
import type { RoleWrite } from "@/api/roles-permissions/roles-permissions.mutations.types";
import { allScopeKindsOf, storedScopeKinds } from "@/api/roles-permissions/roleScope";
import { buildModuleRows, filterRows } from "@/api/roles-permissions/accessCatalog";
import { usePermissionCatalog } from "@/hooks/roles-permissions/usePermissionCatalog";
import { getActiveSessionState } from "@/hooks/useActiveSession";
import type { RoleLevel, RoleWithDirectory } from "@/api/roles-permissions/rolesDirectory.types";
import { useRolesStore } from "@/store/rolesStore";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { DEBOUNCED_WAIT_TIME, SUPER_ADMIN } from "@/components/data/constants";
import { ActionIcon, Box, Flex, Text, Title } from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { IconAlertTriangle, IconDeviceDesktop, IconX } from "@tabler/icons-react";
import { createFileRoute, redirect, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { handleApiSuccess, showErrorAlert } from "@/utils/responseHandler";
import styles from "./assign-permissions.module.scss";

const SOURCE_READ_FAILED = "Couldn't load the role you are duplicating.";
const SCOPE_CARD_TITLE = "Scope";
const SCOPE_CARD_DESCRIPTION =
  "How the role uses each scope box, and whether its holders can be in more than one entity.";

type NewRoleScope = Pick<RoleWrite, "scopeKinds" | "oneEntityOnly">;

// SERV-3572's rule for a new role: every box Not used and the rule off until the admin sets them.
const NEW_ROLE_SCOPE: NewRoleScope = { scopeKinds: {}, oneEntityOnly: false };

const scopeOfSource = (source: RoleWithDirectory | undefined): NewRoleScope =>
  source
    ? { scopeKinds: source.scopeKinds ?? {}, oneEntityOnly: source.oneEntityOnly === true }
    : NEW_ROLE_SCOPE;

const assignPermissionsSearchSchema = z.object({
  /** Duplicate (SERV-3572): the role whose switches, scope kinds and rule the new role copies. */
  duplicateOf: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/roles-permissions/assign-permissions/")({
  component: RolesPermissionsFullComponent,
  validateSearch: assignPermissionsSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });

    // SERV-3572: the role create is taken from super users only (onengine-backend userManagement
    // controller, assertSuperUser), so the editor is closed to anyone else rather than letting
    // them build a role the save will refuse.
    if (getActiveSessionState().userType !== SUPER_ADMIN) {
      throw redirect({ to: "/roles-permissions" });
    }
  },
  // SERV-3572: the draft is persisted so a reload keeps it. Leaving the editor any other way
  // (Cancel, the close button, Back, the sidebar) drops it, so a duplicate or a new role abandoned
  // here does not prefill the next one; a save has already reset it.
  onLeave: () => useRolesStore.getState().resetRoleStore(),
});

function RolesPermissionsFullComponent() {
  const navigate = useNavigate();
  const { duplicateOf } = useSearch({ from: "/roles-permissions/assign-permissions/" });
  const [selectedModuleCode, setSelectedModuleCode] = useState<string | null>(null);
  const [activeModuleIds, setActiveModuleIds] = useState<string[]>([]);
  const [checkedActionIds, setCheckedActionIds] = useState<number[]>([]);
  const [permissionsSearch, setPermissionsSearch] = useState("");
  const [debouncedPermissionsSearch] = useDebouncedValue(permissionsSearch, DEBOUNCED_WAIT_TIME);

  const { role, roleId } = useRolesStore();
  const [isEditRoleModalOpened, editRoleModalHandlers] = useDisclosure(false);
  const [isPreviewOpened, previewHandlers] = useDisclosure(false);

  const effectiveRoleId = role.roleId ?? roleId ?? "";
  const isEditMode = Boolean(role.roleId);
  const duplicateSourceId = isEditMode ? "" : (duplicateOf ?? "");
  // The ticks and modules start from this role: the one being edited, or the one being duplicated.
  const prefillRoleId = isEditMode ? effectiveRoleId : duplicateSourceId;
  const isPrefilled = Boolean(prefillRoleId);
  const {
    data: roleByIdData,
    isError: isPrefillReadFailed,
    isFetching: isPrefillFetching,
    refetch: refetchPrefill,
  } = getRoleById(prefillRoleId, isPrefilled);
  const duplicateSource = duplicateSourceId ? roleByIdData?.data : undefined;
  const isWaitingForSource = Boolean(duplicateSourceId) && !duplicateSource;
  const isSourceReadFailed = isWaitingForSource && isPrefillReadFailed;
  const hasInitializedRoleState = useRef(false);
  // SERV-3592: kept beside the ticks, not in the persisted store, so a draft left behind in the
  // editor can never start the next role. `null` until the admin changes it, so a duplicate shows
  // and carries its source's.
  const [scopeDraft, setScopeDraft] = useState<NewRoleScope | null>(null);
  const scope = scopeDraft ?? scopeOfSource(duplicateSource);

  const {
    data: allModulesData,
    isLoading: isModulesLoading,
    error: modulesError,
  } = useAllModules();
  const modulesData = useMemo(() => allModulesData?.data ?? [], [allModulesData?.data]);
  const {
    catalog,
    isComplete: isCatalogReady,
    isLoading,
    isError: error,
  } = usePermissionCatalog(modulesData);

  useEffect(() => {
    if (!isEditMode) {
      setCheckedActionIds([]);
    }

    const permissionIds = roleByIdData?.data?.permissionIds ?? [];
    if (!permissionIds.length) {
      setCheckedActionIds([]);
    }

    const data = permissionIds
      .map((permissionId) => Number(permissionId))
      .filter((permissionId) => Number.isFinite(permissionId));

    setCheckedActionIds(data);
  }, [roleByIdData]);

  const selectedModuleDetails = useMemo(() => {
    if (!selectedModuleCode) {
      return null;
    }

    return modulesData.find((module) => module.code === selectedModuleCode) ?? null;
  }, [modulesData, selectedModuleCode]);

  // Create and edit read the same catalog, so a role is made with the model it is edited with.
  const allModuleRows = useMemo(
    () => (selectedModuleDetails ? buildModuleRows(selectedModuleDetails.id, catalog) : []),
    [selectedModuleDetails, catalog],
  );
  const moduleRows = useMemo(
    () => filterRows(allModuleRows, debouncedPermissionsSearch),
    [allModuleRows, debouncedPermissionsSearch],
  );

  useEffect(() => {
    hasInitializedRoleState.current = false;
    setActiveModuleIds([]);
    setSelectedModuleCode(null);
  }, [effectiveRoleId]);

  useEffect(() => {
    if (!isEditMode && roleId) {
      useRolesStore.getState().setRoleId("");
    }
  }, [isEditMode, roleId]);

  useEffect(() => {
    if (isPrefilled) {
      return;
    }

    setActiveModuleIds([]);
    setSelectedModuleCode(null);
    setCheckedActionIds([]);
  }, [isPrefilled]);

  useEffect(() => {
    if (
      hasInitializedRoleState.current ||
      !roleByIdData?.data ||
      !modulesData.length ||
      !isPrefilled
    ) {
      return;
    }

    const { moduleIds } = roleByIdData.data;

    if (moduleIds) {
      setActiveModuleIds(moduleIds);
      setSelectedModuleCode(modulesData?.find((data) => data.id === moduleIds[0])?.code || "");
    }

    hasInitializedRoleState.current = true;
  }, [modulesData, roleByIdData, isEditMode]);

  const isModuleEnabled = !!activeModuleIds?.find((id) => id === selectedModuleDetails?.id);

  const activeModules = useMemo(
    () => modulesData.filter((module) => activeModuleIds.includes(module.id)),
    [modulesData, activeModuleIds],
  );

  const modules = useMemo(() => {
    if (!modulesData.length) {
      return [];
    }

    return modulesData.map((module) => {
      const icon = getRouteIcon(module.code as ModuleKey);

      return {
        id: module.id,
        code: module.code,
        label: module.title,
        icon: icon ?? null,
        enabled: !!activeModuleIds?.find((mod) => mod === module.id),
      };
    });
  }, [modulesData, activeModuleIds]);

  const handleEditRole = () => {
    useRolesStore.getState().setRoleId(roleId ?? "");
    useRolesStore.getState().setRole({
      name: role.name,
      description: role.description,
      level: role.level,
      department: role.department,
      roleId: roleId ?? "",
    });
    editRoleModalHandlers.open();
  };

  const handleModuleToggle = (moduleId: string) => {
    setActiveModuleIds((prevActiveModuleId) =>
      prevActiveModuleId.includes(moduleId)
        ? prevActiveModuleId.filter((id) => id !== moduleId)
        : [...prevActiveModuleId, moduleId],
    );
  };

  const renderModulesSidebar = () => {
    if (isModulesLoading) {
      return (
        <Flex
          p="0 var(--spacing-x-small)"
          h="100%"
          w="248px"
          bg="var(--bg-neutral-default)"
          direction="column"
          align="center"
          justify="flex-start"
          gap="var(--spacing-x-small)"
          style={{
            borderRight: "1px solid var(--border-default)",
            overflowY: "auto",
          }}
        >
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="var(--spacing-x-small)"
            style={{ flex: 1 }}
          >
            <Loader size="small" />
            <Text size="sm" c="dimmed">
              Loading modules...
            </Text>
          </Flex>
        </Flex>
      );
    }

    if (modulesError) {
      return (
        <Flex
          p="0 var(--spacing-x-small)"
          h="100%"
          w="248px"
          bg="var(--bg-neutral-default)"
          direction="column"
          align="center"
          justify="flex-start"
          gap="var(--spacing-xx-small)"
          style={{
            borderRight: "1px solid var(--border-default)",
            overflowY: "auto",
          }}
        >
          <Flex
            direction="column"
            align="center"
            justify="flex-start"
            gap="var(--spacing-xx-small)"
            style={{ flex: 1 }}
          >
            <Text size="sm" fw={600} c="red">
              Failed to load modules
            </Text>
            <Text size="xs" c="dimmed" ta="center">
              {modulesError instanceof Error ? modulesError.message : "Unknown error"}
            </Text>
          </Flex>
        </Flex>
      );
    }

    if (!modules.length) {
      return (
        <Flex
          p="0 var(--spacing-x-small)"
          h="100%"
          w="248px"
          bg="var(--bg-neutral-default)"
          direction="column"
          align="center"
          justify="flex-start"
          gap="var(--spacing-xx-small)"
          style={{
            borderRight: "1px solid var(--border-default)",
            overflowY: "auto",
          }}
        >
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="var(--spacing-xx-small)"
            style={{ flex: 1 }}
          >
            <Text size="sm" fw={600}>
              No modules available
            </Text>
            <Text size="xs" c="dimmed" ta="center">
              Modules will appear once they are configured.
            </Text>
          </Flex>
        </Flex>
      );
    }

    return (
      <ModulesSidebar
        modules={modules}
        selectedModuleCode={selectedModuleCode}
        onModuleSelect={setSelectedModuleCode}
        onModuleToggle={handleModuleToggle}
      />
    );
  };

  const handleClose = () => {
    navigate({ to: "/roles-permissions" });
  };

  const { mutate: createRoleMutation, isPending: isCreatingRole } = useCreateRole();
  // SERV-3393: an existing role is edited on its own page now (the Access tab and Edit Details),
  // which hold the switches, scope kinds and one-entity rule a role write re-sends. This page
  // holds none of them, so its edit branch would wipe them. Hidden, not deleted.
  // const { mutate: editRoleMutation, isPending: isEditingRole } = useEditRole();

  const handleCreateRole = () => {
    // Without the whole catalog every id resolves to "unknown module" and the role would be
    // saved with no permissions at all; the button is disabled, this is the backstop.
    if (!isCatalogReady) return;
    // A ticked action under a module that was switched back off grants nothing, so it is dropped
    // here rather than when the module is toggled — the tree of a module the admin never opened
    // is not loaded, which is how those ids used to survive and be re-saved.
    const effectiveIds = effectivePermissionIds(checkedActionIds, activeModuleIds, catalog);

    if (role.roleId) {
      navigate({ to: "/roles-permissions/$roleId", params: { roleId: role.roleId } });
      return;
    }
    if (!role.level) {
      showErrorAlert("Pick the role's level before creating it.", "Failed to Create Role");
      return;
    }
    if (isWaitingForSource) return;

    let write: RoleWrite;
    try {
      const details = {
        name: role.name,
        description: role.description,
        level: role.level as RoleLevel,
        department: role.department || null,
        switches: switchesForSave(effectiveIds, catalog),
      };
      // A duplicate carries the source's scope kinds, one-entity rule and reach, unless the admin
      // changed the scope here; roleWriteFor refuses a source read that left any of them out
      // rather than default them.
      write = duplicateSource
        ? roleWriteFor(duplicateSource, { ...details, ...scopeDraft })
        : { ...details, ...scope, applyToEveryCompany: true, tabs: "explicit" };
    } catch (error) {
      if (!(error instanceof RoleWriteRefusedError)) throw error;
      showErrorAlert(error.message, "Failed to Create Role");
      return;
    }

    createRoleMutation(write, {
      onSuccess: () => {
        handleApiSuccess("The role has been created.", "Role Created Successfully");
      },
    });
  };

  return (
    <>
      <Flex direction="column" w="100%" h="100dvh" bg="var(--neutral-600)" pb="68px">
        {/* Page Header */}
        <Flex
          justify="space-between"
          align="center"
          px="var(--spacing-x-large)"
          py="var(--spacing-medium)"
          bg="var(--bg-neutral-default)"
          style={{
            borderBottom: "1px solid var(--border-default)",
          }}
        >
          <Title
            order={3}
            fw="var(--font-weight-bold)"
            c="var(--text-strong)"
            fz="var(--heading-font-size-h3)"
            lh="var(--heading-line-height-h3)"
          >
            {isEditMode ? "Edit Role & Permissions" : "Add New Role & Permissions"}
          </Title>
          <ActionIcon
            variant="subtle"
            c="var(--icon-default)"
            onClick={handleClose}
            aria-label="Close"
          >
            <IconX size={20} />
          </ActionIcon>
        </Flex>

        {/* Page Content */}
        <Flex
          direction="column"
          p="var(--spacing-x-large)"
          gap="var(--spacing-x-large)"
          style={{ flex: 1, overflow: "auto" }}
        >
          <Card
            title={role.name}
            hasActions={true}
            rightAction="Edit"
            onRightActionClick={handleEditRole}
          >
            <Text size="sm" c="dimmed">
              {role.description}
            </Text>
          </Card>

          {!isEditMode && (
            <Card title={SCOPE_CARD_TITLE} description={SCOPE_CARD_DESCRIPTION}>
              <RoleScopeFields
                scopeKinds={allScopeKindsOf(scope.scopeKinds)}
                oneEntityOnly={scope.oneEntityOnly}
                onScopeKindChange={(box, kind) =>
                  setScopeDraft({
                    ...scope,
                    scopeKinds: storedScopeKinds({
                      ...allScopeKindsOf(scope.scopeKinds),
                      [box]: kind,
                    }),
                  })
                }
                onOneEntityOnlyChange={(oneEntityOnly) =>
                  setScopeDraft({ ...scope, oneEntityOnly })
                }
                disabled={isWaitingForSource}
              />
            </Card>
          )}

          <Flex
            bg="white"
            bdrs="var(--radius-base)"
            direction="column"
            flex={1}
            mih="300px"
            style={{
              overflow: "hidden",
            }}
          >
            <TableCard
              title="Permissions"
              style={{ borderBottom: "1px solid var(--border-default)" }}
              showEditColumns={false}
              showGroupBy={false}
              showSortBy={false}
              showFiltersButton={false}
              showExtraButton={true}
              extraButtonLabel="Preview"
              extraButtonIcon={<IconDeviceDesktop size={16} />}
              extraButtonOnClick={previewHandlers.open}
              onSearchChange={setPermissionsSearch}
            />

            <Flex flex={1} h="calc(100% - 64px)">
              {renderModulesSidebar()}

              <Box
                style={{ flex: 1, overflow: "auto", height: "100%" }}
                className="custom-scrollbar"
              >
                {isLoading ? (
                  <Flex direction="column" align="center" justify="center" h="100%" gap="md">
                    <Loader size="medium" />
                    <Text size="sm" c="dimmed">
                      Loading permissions...
                    </Text>
                  </Flex>
                ) : error ? (
                  <Flex direction="column" align="center" justify="center" h="100%">
                    <Text size="md" fw={600} mb="xs" c="red">
                      Error loading permissions
                    </Text>
                    <Text size="sm" c="dimmed">
                      Check your connection and try again. Nothing was changed.
                    </Text>
                  </Flex>
                ) : !selectedModuleCode ? (
                  <Flex direction="column" align="center" justify="center" h="100%">
                    <Text size="md" fw={600} mb="xs">
                      No Module Selected
                    </Text>
                    <Text size="sm" c="dimmed">
                      Select a module to view and edit permissions
                    </Text>
                  </Flex>
                ) : (
                  <Flex direction="column">
                    {!isModuleEnabled && (
                      <Flex
                        p="var(--spacing-x-small) var(--spacing-x-large)"
                        bg="var(--bg-warning-inverse-default)"
                        gap="var(--spacing-x-small)"
                        align="center"
                      >
                        <IconAlertTriangle
                          size={16}
                          style={{
                            color: "var(--icon-warning-default)",
                            flexShrink: 0,
                          }}
                        />
                        <Text
                          fz="var(--text-font-size-large)"
                          c="var(--text-default)"
                          fw="var(--font-weight-semi-bold)"
                        >
                          You must activate the module to make edits.
                        </Text>
                      </Flex>
                    )}
                    <Box style={{ flex: 1, overflow: "auto" }}>
                      <PermissionsTable
                        key={`${isEditMode ? "edit" : "create"}-${selectedModuleCode ?? "none"}`}
                        rows={moduleRows}
                        cascadeRows={allModuleRows}
                        expandAll={Boolean(debouncedPermissionsSearch.trim())}
                        setCheckedActionIds={setCheckedActionIds}
                        disabled={!isModuleEnabled}
                        checkedActionIds={checkedActionIds}
                      />
                    </Box>
                  </Flex>
                )}
              </Box>
            </Flex>
          </Flex>
        </Flex>

        {/* Footer */}
        <Flex
          justify="flex-end"
          align="center"
          gap="var(--spacing-small)"
          px="var(--spacing-x-large)"
          py="var(--spacing-medium)"
          bg="var(--bg-neutral-default)"
          pos="fixed"
          bottom={0}
          left={0}
          w={"100%"}
          style={{
            borderTop: "1px solid var(--border-default)",
          }}
        >
          {isSourceReadFailed && (
            <div className={styles.sourceReadFailed}>
              <Text className={styles.sourceReadFailedMessage}>{SOURCE_READ_FAILED}</Text>
              <Button
                variant="secondary"
                size="small"
                onClick={() => refetchPrefill()}
                loading={isPrefillFetching}
              >
                Retry
              </Button>
            </div>
          )}
          <Button variant="tertiary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateRole}
            loading={isCreatingRole}
            disabled={!isCatalogReady || isWaitingForSource}
          >
            {role.roleId ? "Update Role" : "Create Role"}
          </Button>
        </Flex>
      </Flex>

      {/* Modals */}
      <AddOrEditRoleModal
        isOpen={isEditRoleModalOpened}
        onClose={editRoleModalHandlers.close}
        type="edit"
      />
      <RolePreviewOverlay
        isOpen={isPreviewOpened}
        onClose={previewHandlers.close}
        roleName={role.name}
        roleDescription={role.description}
        activeModules={activeModules}
        checkedActionIds={checkedActionIds}
      />
    </>
  );
}
